import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination
} from '@mui/material';
import apiService from '../../services/apiService';

const ManualNameReview = () => {
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editingName, setEditingName] = useState(null);
  const [editValueNombre, setEditValueNombre] = useState('');
  const [editValueApellido, setEditValueApellido] = useState('');

  const fetchNames = async (pageNumber, pageSize) => {
    try {
      setLoading(true);
      const response = await apiService.get(`/api/DataExtraction/getPendingNameReviews`, {
        params: {
          page: pageNumber + 1,
          pageSize: pageSize
        }
      });
      setNames(response || []);
      setTotalCount(response?.length || 0);
    } catch (error) {
      showSnackbar('Error al cargar los nombres para revisión', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNames(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startEditing = (name) => {
    setEditingName(name.id);
    setEditValueNombre(name.nombre);
    setEditValueApellido(name.apellido);
  };

  const handleSaveEdit = async (nameId) => {
    try {
      setLoading(true);
      await apiService.post('/api/DataExtraction/updateNameReview', {
        id: nameId,
        nombre: editValueNombre,
        apellido: editValueApellido
      });
      
      showSnackbar('Nombre actualizado exitosamente');
      setEditingName(null);
      fetchNames(page, rowsPerPage);
    } catch (error) {
      showSnackbar('Error al actualizar el nombre', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h4" gutterBottom>
          Revisión Manual de Nombres
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Apellido</TableCell>
                <TableCell>Cédula</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                names.map((name) => (
                  <TableRow key={name.id}>
                    <TableCell>
                      {editingName === name.id ? (
                        <TextField
                          value={editValueNombre}
                          onChange={(e) => setEditValueNombre(e.target.value)}
                          size="small"
                          fullWidth
                        />
                      ) : (
                        name.nombre
                      )}
                    </TableCell>
                    <TableCell>
                      {editingName === name.id ? (
                        <TextField
                          value={editValueApellido}
                          onChange={(e) => setEditValueApellido(e.target.value)}
                          size="small"
                          fullWidth
                        />
                      ) : (
                        name.apellido
                      )}
                    </TableCell>
                    <TableCell>{name.cedula}</TableCell>
                    <TableCell>{name.batchName}</TableCell>
                    <TableCell>
                      {editingName === name.id ? (
                        <Box>
                          <Button
                            onClick={() => handleSaveEdit(name.id)}
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            Guardar
                          </Button>
                          <Button
                            onClick={() => setEditingName(null)}
                            variant="outlined"
                            size="small"
                          >
                            Cancelar
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          onClick={() => startEditing(name)}
                          variant="contained"
                          size="small"
                        >
                          Editar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!loading && names.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, 200]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => {
                const fromValue = page * rowsPerPage + 1;
                const toValue = Math.min((page + 1) * rowsPerPage, count);
                return `${fromValue}-${toValue} de ${count}`;
              }}
            />
          )}
        </TableContainer>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManualNameReview;